import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  CopyPlus,
  Eye,
  FileEdit,
  Link2,
  MoreVertical,
  Plus,
  SlidersHorizontal,
  Trash,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { BulkActionBar } from '@/components/patterns/BulkActionBar';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import {
  type AuthoringStatus,
  authoringStatusLabel,
  authoringStatusMeta,
  StatusPillMenu,
} from '@/components/patterns/StatusPill';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDate, formatTimeWithSeconds } from '@/lib/dateTime';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

import {
  useBulkDeleteQuestionnaires,
  useBulkQuestionnaireStatus,
} from '../api/useBulkQuestionnaires';
import { useChangeQuestionnaireStatus } from '../api/useChangeQuestionnaireStatus';
import { useCreateQuestionnaire } from '../api/useCreateQuestionnaire';
import { useDeleteQuestionnaire } from '../api/useDeleteQuestionnaire';
import { useDuplicateQuestionnaire } from '../api/useDuplicateQuestionnaire';
import { useListQuestionnaires } from '../api/useListQuestionnaires';
import { useRenameQuestionnaire } from '../api/useRenameQuestionnaire';
import { QuestionnaireFeatureIcons } from '../components/QuestionnaireFeatureIcons';
import { RenameQuestionnaireDialog } from '../components/RenameQuestionnaireDialog';
import type { QuestionnaireDto } from '../schemas/questionnaireSchemas';

/** Columns the designer marks sortable. Features has no meaningful order, so it is not one. */
type SortField = 'id' | 'name' | 'createdAt' | 'updatedAt' | 'status';

/**
 * The short reference an admin reads out or searches for.
 *
 * Our ids are GUIDs; the designer's mock shows `#7045`. A GUID is unreadable in a table and
 * unrepeatable over a phone, so the first eight characters are shown — enough to identify a row
 * within a tenant's list, and part of the real id rather than a separate counter that would need its
 * own column and a backfill. The full id is on the tooltip for anyone who needs to paste it.
 */
function shortId(id: string): string {
  return `#${id.slice(0, 8).toUpperCase()}`;
}

export function QuestionnairesListScreen() {
  const navigate = useNavigate();
  const { data, isLoading } = useListQuestionnaires({ page: 1, pageSize: 50 });
  const createQuestionnaire = useCreateQuestionnaire();
  const deleteQuestionnaire = useDeleteQuestionnaire();
  const changeStatus = useChangeQuestionnaireStatus();
  const renameQuestionnaire = useRenameQuestionnaire();
  const duplicateQuestionnaire = useDuplicateQuestionnaire();

  const bulkStatus = useBulkQuestionnaireStatus();
  const bulkDelete = useBulkDeleteQuestionnaires();
  const bulkBusy = bulkStatus.isPending || bulkDelete.isPending;

  const [toDelete, setToDelete] = useState<QuestionnaireDto | null>(null);
  const [toRename, setToRename] = useState<QuestionnaireDto | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // Selected row ids. A Set of ids rather than of rows, so a refetch that returns new object
  // identities does not silently drop the selection.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Pending bulk actions — held until the user confirms, never applied straight from the bar.
  const [bulkTarget, setBulkTarget] = useState<AuthoringStatus | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // A single row's pending status change. Held rather than applied on click, for the same reason the
  // bulk actions are: publishing makes a questionnaire selectable in live workflows, and archiving
  // takes it away from them — neither should happen from one stray click on a pill.
  const [statusRequest, setStatusRequest] = useState<{
    questionnaire: QuestionnaireDto;
    target: AuthoringStatus;
  } | null>(null);

  const questionnaires = data?.items ?? [];
  const selectedCount = selectedIds.size;
  const allSelected =
    questionnaires.length > 0 && questionnaires.every((q) => selectedIds.has(q.id));

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const q of questionnaires) {
        if (allSelected) next.delete(q.id);
        else next.add(q.id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  /** Runs only after the confirmation dialog. */
  function confirmBulkStatus() {
    if (!bulkTarget) return;
    const target = bulkTarget;
    const items = questionnaires
      .filter((q) => selectedIds.has(q.id))
      .map((q) => ({ id: q.id, status: q.status }));

    if (items.length === 0) {
      setBulkTarget(null);
      return;
    }

    bulkStatus.mutate(
      { items, target },
      {
        onSuccess: (r) => {
          if (r.failed === 0) {
            toast.success(
              `${r.succeeded} questionnaire${r.succeeded === 1 ? '' : 's'} set to ${authoringStatusLabel(target).toLowerCase()}`,
            );
          } else {
            toast.error(`${r.succeeded} updated, ${r.failed} failed`);
          }
          clearSelection();
          setBulkTarget(null);
        },
        onError: () => {
          toast.error('Could not update the selected questionnaires');
          setBulkTarget(null);
        },
      },
    );
  }

  /** Runs only after the confirmation dialog. Permanent — no undo. */
  function confirmBulkDelete() {
    const ids = questionnaires.filter((q) => selectedIds.has(q.id)).map((q) => q.id);
    if (ids.length === 0) {
      setBulkDeleteOpen(false);
      return;
    }

    bulkDelete.mutate(ids, {
      onSuccess: (r) => {
        if (r.failed === 0) {
          toast.success(`${r.succeeded} questionnaire${r.succeeded === 1 ? '' : 's'} deleted`);
        } else {
          toast.error(`${r.succeeded} deleted, ${r.failed} failed`);
        }
        clearSelection();
        setBulkDeleteOpen(false);
      },
      onError: () => {
        toast.error('Could not delete the selected questionnaires');
        setBulkDeleteOpen(false);
      },
    });
  }

  /**
   * Third click clears the sort rather than cycling back to ascending — the designer's behaviour,
   * and it is the only way back to the server's own "most recently updated first" ordering, which is
   * the useful default.
   */
  function handleSort(field: SortField) {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder('asc');
      return;
    }
    if (sortOrder === 'asc') {
      setSortOrder('desc');
      return;
    }
    setSortField(null);
    setSortOrder('asc');
  }

  const sorted = useMemo(() => {
    if (!sortField) return questionnaires;

    return [...questionnaires].sort((a, b) => {
      let result: number;
      if (sortField === 'createdAt' || sortField === 'updatedAt') {
        // Compared as timestamps, not strings: "19 Aug" vs "2 Sep" sorts wrongly as text.
        result = new Date(a[sortField]).getTime() - new Date(b[sortField]).getTime();
      } else if (sortField === 'status') {
        // Sorted by the LABEL the reader sees ("Published"), not the stored value ("Active") —
        // otherwise the column sorts into an order that does not match what is on screen.
        result = authoringStatusLabel(a.status).localeCompare(authoringStatusLabel(b.status));
      } else {
        result = a[sortField].localeCompare(b[sortField], undefined, { numeric: true });
      }
      return sortOrder === 'asc' ? result : -result;
    });
  }, [questionnaires, sortField, sortOrder]);

  function handleCreate() {
    createQuestionnaire.mutate(
      { name: 'New Questionnaire' },
      {
        onSuccess: (q) => navigate(`/questionnaires/${q.id}/builder`),
        onError: () => toast.error('Failed to create questionnaire'),
      },
    );
  }

  /** Runs only after the confirmation dialog. */
  function confirmStatusChange() {
    if (!statusRequest) return;
    const { questionnaire: q, target } = statusRequest;

    changeStatus.mutate(
      { questionnaireId: q.id, status: target },
      {
        onSuccess: () => {
          toast.success(`"${q.name}" is now ${authoringStatusLabel(target).toLowerCase()}`);
          setStatusRequest(null);
        },
        onError: () => {
          toast.error('Failed to update status');
          setStatusRequest(null);
        },
      },
    );
  }

  async function copyLink(q: QuestionnaireDto) {
    const url = `${window.location.origin}/questionnaires/${q.id}/builder`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Questionnaire link copied');
    } catch {
      // Clipboard access is refused outside a secure context and in some embedded browsers. Saying
      // so beats a button that silently does nothing.
      toast.error("Couldn't copy the link — copy it from the address bar instead.");
    }
  }

  /** Copies the whole tree server-side and stays on the list, with the copy now in it. */
  function duplicate(q: QuestionnaireDto) {
    duplicateQuestionnaire.mutate(q.id, {
      onSuccess: (copy) => toast.success(`Duplicated as "${copy.name}"`),
      onError: () => toast.error('Failed to duplicate questionnaire'),
    });
  }

  function confirmRename(name: string) {
    if (!toRename) return;
    renameQuestionnaire.mutate(
      { questionnaireId: toRename.id, name },
      {
        onSuccess: () => {
          toast.success('Questionnaire renamed');
          setToRename(null);
        },
        onError: () => {
          toast.error('Failed to rename questionnaire');
          setToRename(null);
        },
      },
    );
  }

  function confirmDelete() {
    if (!toDelete) return;
    deleteQuestionnaire.mutate(toDelete.id, {
      onSuccess: () => {
        toast.success('Questionnaire deleted');
        setToDelete(null);
      },
      onError: () => {
        toast.error('Failed to delete questionnaire');
        setToDelete(null);
      },
    });
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return <ChevronsUpDown size={12} className="text-gray-400" />;
    }
    return sortOrder === 'asc' ? (
      <ChevronUp size={12} className="text-primary" />
    ) : (
      <ChevronDown size={12} className="text-primary" />
    );
  }

  return (
    // ONE header row — heading, description and the action button together, as the designer has it.
    // The page used to print "Questionnaires" in the Topbar and "Questionnaire" again as an <h1> here,
    // two headings deep, with the description and button under the duplicate.
    <AppShell
      title="Questionnaire"
      subtitle="Design data collection questionnaire with the fields you need, and add them to any workflow step"
      actions={
        <button
          type="button"
          onClick={handleCreate}
          disabled={createQuestionnaire.isPending}
          className="flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-[length:var(--text-14)] font-medium text-white transition-colors hover:bg-[#0038CC] disabled:opacity-50"
        >
          <Plus size={16} /> Add Questionnaire
        </button>
      }
    >
      <div className="flex h-full w-full flex-col text-black-logo">
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          {/* ring-[#D0D4E4] and a #F8FAFE head — the designer's own
              `.sc-questionnaires-table__wrapper` / `__head`, not the darker border we had. */}
          <div className="min-h-0 flex-1 overflow-auto rounded-lg ring-1 ring-[#D0D4E4]">
            <table className="w-full min-w-[900px] border-collapse">
              <thead className="sticky top-0 z-10 bg-[#F8FAFE] text-left text-[#041A3A]">
                <tr className="text-[length:var(--text-14)]">
                  <th className={cn(TH_CLASS, 'w-10 text-center')}>
                    <input
                      type="checkbox"
                      aria-label="Select all questionnaires on this page"
                      className="h-4 w-4 cursor-pointer align-middle accent-primary"
                      checked={allSelected}
                      // Indeterminate cannot be set from JSX — it is a DOM property, not an
                      // attribute — so it is written through the ref. Without it a partial selection
                      // shows an unticked box, which reads as "nothing selected".
                      ref={(el) => {
                        if (el) el.indeterminate = selectedCount > 0 && !allSelected;
                      }}
                      onChange={toggleAll}
                    />
                  </th>
                  <SortableTh field="id" onSort={handleSort} icon={<SortIcon field="id" />}>
                    Id
                  </SortableTh>
                  <SortableTh field="name" onSort={handleSort} icon={<SortIcon field="name" />}>
                    Questionnaire
                  </SortableTh>
                  <Th>Features</Th>
                  <SortableTh
                    field="createdAt"
                    onSort={handleSort}
                    icon={<SortIcon field="createdAt" />}
                  >
                    Created
                  </SortableTh>
                  <SortableTh
                    field="updatedAt"
                    onSort={handleSort}
                    icon={<SortIcon field="updatedAt" />}
                  >
                    Last Updated
                  </SortableTh>
                  <SortableTh
                    field="status"
                    center
                    onSort={handleSort}
                    icon={<SortIcon field="status" />}
                  >
                    Status
                  </SortableTh>
                  <Th center>Action</Th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-3.5 py-10 text-center text-[#6B7280]">
                      Loading…
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3.5 py-10 text-center text-[#6B7280]">
                      No questionnaires yet. Click{' '}
                      <span className="font-medium">Add Questionnaire</span> to create one.
                    </td>
                  </tr>
                ) : (
                  sorted.map((q) => (
                    // THE WHOLE ROW OPENS THE BUILDER, as the designer's does. Previously only the
                    // name was clickable, so the obvious gesture — clicking the row — did nothing.
                    // The status pill and the action cell stop propagation, or changing a status
                    // would also navigate away from the list you were working in.
                    <tr
                      key={q.id}
                      onClick={(e) => {
                        // ONE handler on the row, which ignores anything that started inside a
                        // control. The alternative — stopPropagation on the status and action cells —
                        // needed a click handler on two <td>s that did nothing but block, which is an
                        // interactive element with no keyboard behaviour for a screen reader to find.
                        // Here the row is the only handler and the buttons inside stay plain buttons.
                        if ((e.target as HTMLElement).closest('button,a,[role="menuitem"]')) return;
                        navigate(`/questionnaires/${q.id}/builder`);
                      }}
                      className={cn(
                        'cursor-pointer border-t border-[#CBD5E1] text-[#041A3A] transition-colors',
                        selectedIds.has(q.id) ? 'bg-primary/5' : 'hover:bg-gray-50/80',
                      )}
                    >
                      <Td center>
                        <input
                          type="checkbox"
                          aria-label={`Select ${q.name}`}
                          className="h-4 w-4 cursor-pointer align-middle accent-primary"
                          checked={selectedIds.has(q.id)}
                          onChange={() => toggleRow(q.id)}
                        />
                      </Td>
                      <Td muted title={q.id}>
                        {shortId(q.id)}
                      </Td>
                      <Td medium>
                        <span className="block max-w-[320px] truncate" title={q.name}>
                          {q.name}
                        </span>
                      </Td>
                      <Td>
                        <QuestionnaireFeatureIcons fieldTypes={q.fieldTypes} />
                      </Td>
                      <TimestampTd value={q.createdAt} />
                      <TimestampTd value={q.updatedAt} />

                      <Td center>
                        {/* Raises a request; the confirmation dialog applies it. */}
                        <StatusPillMenu
                          status={q.status}
                          onSelect={(next) => setStatusRequest({ questionnaire: q, target: next })}
                        />
                      </Td>

                      <Td center>
                        <div className="inline-flex items-center justify-center gap-2">
                          <button
                            type="button"
                            aria-label={`Copy link to ${q.name}`}
                            title="Copy link"
                            onClick={() => void copyLink(q)}
                            className="p-1 text-primary transition-opacity hover:opacity-80"
                          >
                            <Link2 size={16} />
                          </button>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label={`Actions for ${q.name}`}
                                className="cursor-pointer p-1 text-gray30 transition-colors hover:text-black-logo"
                              >
                                <MoreVertical size={14} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <MenuItem
                                onClick={() => navigate(`/questionnaires/${q.id}/preview`)}
                                icon={<Eye size={14} />}
                                label="Preview"
                              />
                              <MenuItem
                                onClick={() => navigate(`/questionnaires/${q.id}/builder`)}
                                icon={<SlidersHorizontal size={14} />}
                                label="Edit"
                              />
                              <MenuItem
                                onClick={() => setToRename(q)}
                                icon={<FileEdit size={14} />}
                                label="Rename"
                              />
                              <MenuItem
                                onClick={() => duplicate(q)}
                                icon={<CopyPlus size={14} />}
                                label="Duplicate"
                              />
                              {/* Delete is NOT in the designer's menu — its delete path is a bulk
                                  toolbar behind row checkboxes, which is not built. Leaving it out
                                  would mean a questionnaire could never be removed at all, so it is
                                  here, last and in red. */}
                              <MenuItem
                                onClick={() => setToDelete(q)}
                                icon={<Trash size={14} />}
                                label="Delete"
                                destructive
                              />
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <BulkActionBar
        selectedCount={selectedCount}
        noun="questionnaire"
        busy={bulkBusy}
        onSetStatus={setBulkTarget}
        onDelete={() => setBulkDeleteOpen(true)}
        onClear={clearSelection}
      />

      {/* Single-row status change — the SAME alert popup the workflows list uses, naming the
          questionnaire and the target status. */}
      <ConfirmDialog
        open={!!statusRequest}
        variant="alert"
        title="Are you sure?"
        description={
          statusRequest ? (
            <>
              Are you sure you want to change the status of{' '}
              <span className="font-semibold text-black-logo">
                "{statusRequest.questionnaire.name}"
              </span>{' '}
              to{' '}
              <span className="font-semibold text-black-logo">
                "{authoringStatusMeta(statusRequest.target).label}"
              </span>
              ?
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Confirm"
        pendingLabel="Updating…"
        isPending={changeStatus.isPending}
        onConfirm={confirmStatusChange}
        onCancel={() => setStatusRequest(null)}
      />

      {/* Bulk status change — the SAME alert popup the workflows list uses, with the count and the
          target status bolded. Nothing is applied from the bar directly: a bar click that silently
          republished ten questionnaires would be unrecoverable. */}
      <ConfirmDialog
        open={!!bulkTarget}
        variant="alert"
        title="Are you sure?"
        description={
          bulkTarget ? (
            <>
              Are you sure you want to change the status of{' '}
              <span className="font-semibold text-black-logo">
                {selectedCount} questionnaire{selectedCount === 1 ? '' : 's'}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-black-logo">
                "{authoringStatusMeta(bulkTarget).label}"
              </span>
              ?
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Confirm"
        pendingLabel="Updating…"
        isPending={bulkStatus.isPending}
        onConfirm={confirmBulkStatus}
        onCancel={() => setBulkTarget(null)}
      />

      {/* Bulk delete — same popup, with the "cannot be undone" line and a destructive Confirm. */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        variant="alert"
        title="Are you sure?"
        description={
          <>
            Are you sure you want to permanently delete{' '}
            <span className="font-semibold text-black-logo">
              {selectedCount} questionnaire{selectedCount === 1 ? '' : 's'}
            </span>
            , with all their sections and fields? This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        destructive
        isPending={bulkDelete.isPending}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      <RenameQuestionnaireDialog
        open={!!toRename}
        currentName={toRename?.name ?? ''}
        isPending={renameQuestionnaire.isPending}
        onCancel={() => setToRename(null)}
        onConfirm={confirmRename}
      />

      <ConfirmDialog
        open={!!toDelete}
        title={`Delete "${toDelete?.name}"?`}
        description="This permanently deletes the questionnaire and all its sections and fields. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={deleteQuestionnaire.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </AppShell>
  );
}

// ── Cells ─────────────────────────────────────────────────────────────────────
// Extracted so the designer's clamp-based padding is written once instead of on every one of the
// fourteen cells this table renders.

const TH_CLASS = 'px-[clamp(8px,0.7vw,14px)] py-[clamp(8px,0.75vh,12px)] font-medium';
const TD_CLASS =
  'px-[clamp(10px,0.875vw,14px)] py-[clamp(12px,1vh,16px)] text-[length:var(--text-14)]';

function Th({ children, center }: { children: React.ReactNode; center?: boolean }) {
  return <th className={cn(TH_CLASS, center && 'text-center')}>{children}</th>;
}

function SortableTh({
  children,
  field,
  center,
  icon,
  onSort,
}: {
  children: React.ReactNode;
  field: SortField;
  center?: boolean;
  icon: React.ReactNode;
  onSort: (field: SortField) => void;
}) {
  return (
    <th className={cn(TH_CLASS, center && 'text-center')}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          'inline-flex select-none items-center gap-1 font-medium',
          center && 'justify-center',
        )}
      >
        {children}
        {icon}
      </button>
    </th>
  );
}

function Td({
  children,
  muted,
  medium,
  center,
  title,
}: {
  children: React.ReactNode;
  muted?: boolean;
  medium?: boolean;
  center?: boolean;
  title?: string;
}) {
  return (
    <td
      className={cn(
        TD_CLASS,
        muted && 'text-[#041A3A]',
        medium && 'font-medium',
        center && 'text-center',
      )}
      title={title}
    >
      {children}
    </td>
  );
}

/**
 * Date on the first line, time WITH SECONDS on the second — the designer's stacked cell.
 *
 * Absolute, not relative ("3 days ago", which this column used to show): an admin reconciling a
 * questionnaire against a compliance change needs the date, and "3 days ago" reads differently
 * depending on when the page was opened. Seconds matter here specifically because several
 * questionnaires drafted in one sitting share a minute, and the minute alone then cannot tell you
 * which came first.
 */
function TimestampTd({ value }: { value: string }) {
  return (
    <td className={cn(TD_CLASS, 'whitespace-nowrap leading-snug text-[#041A3A]')}>
      <div>{formatDate(value)}</div>
      <div className="text-[11px] text-gray66">{formatTimeWithSeconds(value)}</div>
    </td>
  );
}

function MenuItem({
  onClick,
  icon,
  label,
  destructive,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  destructive?: boolean;
}) {
  return (
    <DropdownMenuItem
      onClick={onClick}
      className={cn(
        'flex items-center gap-3.5 px-4 py-2.5 text-[14px] font-medium',
        destructive ? 'text-destructive' : 'text-[#2D3748]',
      )}
    >
      {icon}
      <span>{label}</span>
    </DropdownMenuItem>
  );
}
